import { useEffect, useState } from 'react';

export default function QuestionEditor({ initialQuestion = null, onSave, onCancel, busy }) {
  const [questionText, setQuestionText] = useState('');
  const [answerMode, setAnswerMode] = useState('unique');
  const [explication, setExplication] = useState('');
  const [difficulte, setDifficulte] = useState('moyen');
  const [notion, setNotion] = useState('');
  const [source, setSource] = useState('');
  const [timeSensitive, setTimeSensitive] = useState(false);
  const [choices, setChoices] = useState([
    { text: '', is_correct: true },
    { text: '', is_correct: false },
    { text: '', is_correct: false },
    { text: '', is_correct: false },
  ]);

  useEffect(() => {
    if (!initialQuestion) {
      setAnswerMode('unique');
      setQuestionText('');
      setExplication('');
      setDifficulte('moyen');
      setNotion('');
      setSource('');
      setTimeSensitive(false);
      setChoices([
        { text: '', is_correct: true },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
      ]);
      return;
    }

    const multiple =
      initialQuestion.type_question === 'qcm_multiple' ||
      (initialQuestion.choices || []).filter((choice) => choice.is_correct).length > 1;
    setAnswerMode(multiple ? 'multiple' : 'unique');
    setQuestionText(initialQuestion.question_text || '');
    setExplication(initialQuestion.explication || '');
    setDifficulte(initialQuestion.difficulte || 'moyen');
    setNotion(initialQuestion.notion_cible || '');
    setSource(initialQuestion.source_pedagogique || '');
    setTimeSensitive(Boolean(initialQuestion.is_time_sensitive));
    setChoices(
      (initialQuestion.choices || []).map((choice) => ({
        text: choice.choice_text ?? choice.text ?? '',
        is_correct: Boolean(choice.is_correct),
      }))
    );
  }, [initialQuestion]);

  function setCorrect(index) {
    setChoices((previous) =>
      previous.map((choice, choiceIndex) => ({
        ...choice,
        is_correct: answerMode === 'multiple'
          ? choiceIndex === index ? !choice.is_correct : choice.is_correct
          : choiceIndex === index,
      }))
    );
  }

  function changeAnswerMode(mode) {
    if (mode === answerMode) return;
    setAnswerMode(mode);
    if (mode === 'unique') {
      setChoices((previous) => {
        const firstCorrect = Math.max(0, previous.findIndex((choice) => choice.is_correct));
        return previous.map((choice, index) => ({
          ...choice, is_correct: index === firstCorrect,
        }));
      });
    }
  }

  function setChoiceText(index, value) {
    setChoices((previous) =>
      previous.map((choice, choiceIndex) =>
        choiceIndex === index ? { ...choice, text: value } : choice
      )
    );
  }

  function addChoice() {
    if (choices.length >= 8) return;
    setChoices((previous) => [...previous, { text: '', is_correct: false }]);
  }

  function removeChoice(index) {
    if (choices.length <= 2) return;
    const wasCorrect = choices[index]?.is_correct;
    const next = choices.filter((_, choiceIndex) => choiceIndex !== index);
    if (wasCorrect && next.length) next[0] = { ...next[0], is_correct: true };
    setChoices(next);
  }

  async function submit(event) {
    event.preventDefault();
    await onSave({
      question_text: questionText,
      explication: explication || null,
      difficulte: difficulte || null,
      notion_cible: notion || null,
      source_pedagogique: source || null,
      type_question: answerMode === 'multiple' ? 'qcm_multiple' : 'qcm',
      is_time_sensitive: timeSensitive,
      choices: choices.map((choice) => ({
        text: choice.text,
        is_correct: choice.is_correct,
      })),
    });
  }

  return (
    <form className="bo-form bo-question-editor" onSubmit={submit}>
      <label className="bo-span-2">
        Énoncé
        <textarea
          className="bo-input"
          rows="3"
          value={questionText}
          onChange={(event) => setQuestionText(event.target.value)}
          required
        />
      </label>
      <label>
        Difficulté
        <select
          className="bo-input"
          value={difficulte}
          onChange={(event) => setDifficulte(event.target.value)}
        >
          <option value="facile">Facile</option>
          <option value="moyen">Moyen</option>
          <option value="difficile">Difficile</option>
        </select>
      </label>
      <label>
        Notion cible
        <input
          className="bo-input"
          value={notion}
          onChange={(event) => setNotion(event.target.value)}
        />
      </label>
      <label className="bo-span-2">
        Explication / correction
        <textarea
          className="bo-input"
          rows="3"
          value={explication}
          onChange={(event) => setExplication(event.target.value)}
        />
      </label>
      <label className="bo-span-2">
        Source pédagogique
        <input
          className="bo-input"
          value={source}
          onChange={(event) => setSource(event.target.value)}
        />
      </label>

      <div className="bo-span-2 bo-choice-list">
        <div className="bo-answer-mode" role="group" aria-label="Type de réponse">
          <span className="bo-answer-mode__label">Type de réponse attendue</span>
          <div className="bo-answer-mode__buttons">
            <button type="button" className={answerMode === 'unique' ? 'bo-answer-mode__button is-active' : 'bo-answer-mode__button'}
              aria-pressed={answerMode === 'unique'} onClick={() => changeAnswerMode('unique')}>
              Réponse unique
            </button>
            <button type="button" className={answerMode === 'multiple' ? 'bo-answer-mode__button is-active' : 'bo-answer-mode__button'}
              aria-pressed={answerMode === 'multiple'} onClick={() => changeAnswerMode('multiple')}>
              Réponses multiples
            </button>
          </div>
          <small className="bo-muted">
            {answerMode === 'multiple'
              ? 'Cochez au moins deux bonnes réponses. Le candidat devra toutes les sélectionner, sans erreur.'
              : 'Sélectionnez l’unique bonne réponse.'}
          </small>
        </div>
        <div className="bo-row bo-row--between">
          <strong>Propositions</strong>
          <button type="button" className="bo-button bo-button--ghost" onClick={addChoice}>
            + Ajouter un choix
          </button>
        </div>
        {choices.map((choice, index) => (
          <div className="bo-choice-row" key={index}>
            <input
              type={answerMode === "multiple" ? "checkbox" : "radio"}
              name="correct-choice"
              checked={choice.is_correct}
              onChange={() => setCorrect(index)}
              aria-label={`Choix ${index + 1} correct`}
            />
            <input
              className="bo-input"
              value={choice.text}
              onChange={(event) => setChoiceText(index, event.target.value)}
              placeholder={`Choix ${index + 1}`}
              required
            />
            <button
              type="button"
              className="bo-icon-button"
              onClick={() => removeChoice(index)}
              disabled={choices.length <= 2}
              title="Retirer ce choix"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <label className="bo-checkbox bo-span-2">
        <input
          type="checkbox"
          checked={timeSensitive}
          onChange={(event) => setTimeSensitive(event.target.checked)}
        />
        Question sensible au temps / à l’actualité
      </label>

      <div className="bo-actions bo-span-2">
        {onCancel && (
          <button type="button" className="bo-button bo-button--ghost" onClick={onCancel}>
            Annuler
          </button>
        )}
        <button className="bo-button bo-button--primary" disabled={busy ||
          (answerMode === 'multiple' && choices.filter((choice) => choice.is_correct).length < 2)}>

          {busy ? 'Enregistrement…' : initialQuestion ? 'Enregistrer' : 'Ajouter la question'}
        </button>
      </div>
    </form>
  );
}
